import {
  LabeledButton,
  LabeledButtonContent,
  LabeledButtonLabel,
} from "@babackapp/customer-ui/components/button";
import { DateSelect } from "@babackapp/customer-ui/components/date-select";
import { Label } from "@babackapp/customer-ui/components/label";
import {
  Option,
  OptionAction,
  OptionContent,
  OptionDescription,
  OptionIcon,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { RadioGroupItem } from "@babackapp/customer-ui/components/radio-group";
import { ChevronsUpDown, MailboxIcon } from "lucide-react";
import { Controller, type Control } from "react-hook-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";

type FormValues = {
  returnMode: string;
  pickupDate?: string;
  storeId?: string;
};

type Props = {
  control: Control<FormValues>;
  selected: boolean;
  error?: string;
};

/** Parse a YYYY-MM-DD string as a local-timezone Date (avoids UTC-midnight offset). */
function localDateFromString(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Serialize a Date to YYYY-MM-DD using local date components. */
function localDateToString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function CarrierOption({ control, selected, error }: Props) {
  const t = useCustomText();
  return (
    <Label htmlFor="carrier">
      <Option>
        <OptionIcon>
          <MailboxIcon />
        </OptionIcon>
        <OptionTitle>{t("returnMode.carrier.title")}</OptionTitle>
        <OptionDescription>
          {t("returnMode.carrier.description")}
        </OptionDescription>
        <OptionContent>
          {selected && (
            <Controller
              control={control}
              name="pickupDate"
              render={({ field }) => (
                <DateSelect
                  className="w-full"
                  selected={field.value ? localDateFromString(field.value) : undefined}
                  onSelect={(date) =>
                    field.onChange(date ? localDateToString(date) : undefined)
                  }
                >
                  <LabeledButton variant="outline" aria-invalid={!!error}>
                    <LabeledButtonLabel>{t("returnMode.carrier.pickupDate")}</LabeledButtonLabel>
                    <LabeledButtonContent>
                      {field.value
                        ? localDateFromString(field.value).toLocaleDateString("fr-FR")
                        : t("returnMode.carrier.noDateSelected")}
                    </LabeledButtonContent>
                    <ChevronsUpDown />
                  </LabeledButton>
                </DateSelect>
              )}
            />
          )}
        </OptionContent>
        <OptionAction>
          <RadioGroupItem value="carrier" id="carrier" />
        </OptionAction>
      </Option>
    </Label>
  );
}
